import { planCopilotMessageParts } from "@/features/copilot/lib/copilot-message-plan";
import type { CopilotActionView } from "@/features/copilot/schemas/copilot.schema";

interface AssistantCompletionMessage {
  role: string;
  status?: { type: string };
  content: ReadonlyArray<{ type: string; text?: string; toolCallId?: string }>;
}

/// Text only from the answer role: "Let me check..." narration written before a tool call is
/// transient and would make the live region read half-thoughts aloud.
export function completedAssistantText(
  wasRunning: boolean,
  isRunning: boolean,
  message: AssistantCompletionMessage | undefined,
): string | null {
  if (!wasRunning || isRunning || !message || message.role !== "assistant") return null;
  if (message.status?.type !== "complete") return null;
  const roles = planCopilotMessageParts(message.content);
  const text = message.content
    .filter((part, index) => part.type === "text" && roles[index] === "answer" && part.text)
    .map((part) => part.text?.trim() ?? "")
    .filter(Boolean)
    .join(" ");
  return text || null;
}

/// Said once when a run begins; the thinking indicator itself mounts and unmounts between steps,
/// so it cannot carry an aria-live region.
export function runStartAnnouncement(wasRunning: boolean, isRunning: boolean): string | null {
  return !wasRunning && isRunning ? "Hand is working on it" : null;
}

/// A run that stops on a confirmation card is silent otherwise, and the person may not see it.
export function pendingDecisionAnnouncement(
  wasRunning: boolean,
  isRunning: boolean,
  message: AssistantCompletionMessage | undefined,
  actions: ReadonlyMap<string, CopilotActionView>,
): string | null {
  if (!wasRunning || isRunning || !message || message.role !== "assistant") return null;
  for (const part of message.content) {
    if (part.type !== "tool-call" || !part.toolCallId) continue;
    const action = actions.get(part.toolCallId);
    if (action?.status === "pending") return `Hand needs your decision: ${action.summary.title}`;
  }
  return null;
}
